import { Injectable, inject } from '@angular/core';

import { FixtureViewType } from '@cds/betting-offer';
import { BaselineSelectionStrategy, EventOptionGroup, EventParticipant, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { HappeningType, MarketType, PeriodType } from '@frontend/sports/betting-offer/feature/types';
import { BettingOfferConfig, GridConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { GridEvent, Group } from '@frontend/sports/grid/core/feature/model';
import { Decimal } from 'decimal.js';
import { cloneDeep, orderBy } from 'lodash-es';

import { extractSpreadValue, selectByOddsKeyMarginRule, selectLowestSpreadItem } from '../../option-group/baseline-selector.util';

@Injectable({ providedIn: 'root' })
export class OptionGroupService {
    private gridConfig = inject(GridConfig);
    private offerGroupingService = inject(OfferGroupingService);
    private offerConfig = inject(BettingOfferConfig);

    fallbackMarketsHeaderInfo: { [key: string]: string } = {};

    get(optionGroups: EventOptionGroup[], sportId: SportConstant, isLive: boolean, groupId?: string | number): EventOptionGroup | undefined {
        if (!optionGroups) {
            return;
        }

        const strategy = this.offerConfig.balancedLineSelectionStrategyBySport[sportId];
        const shouldUseOddsKey = strategy === BaselineSelectionStrategy.OddsKey && !isLive;

        return shouldUseOddsKey ? this.getOptionGroupByOddsKey(optionGroups, groupId) : this.getOptionGroupBySpread(optionGroups, groupId);
    }

    private getOptionGroupBySpread(optionGroups: EventOptionGroup[], groupId?: string | number): EventOptionGroup | undefined {
        return selectLowestSpreadItem<EventOptionGroup>(
            optionGroups,
            (group) => extractSpreadValue(group),
            groupId !== undefined ? (group) => group.hasGroup(groupId) : (group) => group.hasAnyGroup(),
        );
    }

    private getOptionGroupByOddsKey(optionGroups: EventOptionGroup[], groupId?: string | number): EventOptionGroup | undefined {
        const filteredGroups = optionGroups.filter((optionGroup) =>
            groupId !== undefined ? optionGroup.hasGroup(groupId) : optionGroup.hasAnyGroup(),
        );

        const result = selectByOddsKeyMarginRule(
            filteredGroups,
            (group) => group.optionSpread,
            (group) => group.optionOddsKey,
        );

        return result ?? this.getOptionGroupBySpread(optionGroups, groupId);
    }

    orderBasedOnParticipants(
        eventOptionGroup: EventOptionGroup | undefined,
        participants: EventParticipant[],
        viewType: FixtureViewType,
    ): EventOptionGroup | undefined {
        if (viewType !== FixtureViewType.American || !eventOptionGroup) {
            return eventOptionGroup;
        }

        const getIndex = (id: number) => participants.findIndex((p) => p.fixtureParticipantId === id);

        const sortedOptions = orderBy(eventOptionGroup.options, [
            (option) => {
                return option.optionPlayerId === undefined ? undefined : getIndex(option.optionPlayerId);
            },
        ]);

        const optionGroup = cloneDeep(eventOptionGroup);
        optionGroup.options = sortedOptions;

        return optionGroup;
    }

    private hasNonBettableOdds(eventOptionGroup: EventOptionGroup | undefined): boolean {
        return !!eventOptionGroup?.options.some((element) => element.nativePrice.odds < new Decimal(this.gridConfig.fallbackMarketOddsThreshold));
    }

    private isBettableOptionGroup(optionGroup: EventOptionGroup | undefined): boolean {
        return !!(optionGroup && optionGroup.online && optionGroup.options?.length && optionGroup.options.some((opt) => opt.online));
    }

    private setFallbackGroupsNames(event: GridEvent, gridIds: string[]): void {
        this.offerGroupingService.getGridGrouping(event.sport.id).subscribe((gridGroupConfig) => {
            gridGroupConfig?.groups?.map((group) => {
                if (gridIds.includes(group.id)) {
                    this.fallbackMarketsHeaderInfo[group.id] = group.name;
                }
            });
        });
    }

    getFallbackOptionGroup(
        group: Group,
        event: GridEvent,
        optionGroup: EventOptionGroup | undefined,
    ): { optionGroup: EventOptionGroup | undefined; isFallbackMarket: boolean; fallbackMarketName: string; isNextGoalMarket: boolean } | undefined {
        let fallbackMarketName = '';

        const fallbackGridGroupIds = group?.fallbackGridGroupIds ?? [];

        if (!this.showFallbackMarkets(fallbackGridGroupIds, optionGroup, event)) {
            return;
        }

        this.setFallbackGroupsNames(event, fallbackGridGroupIds);

        const availableOptionGroup = fallbackGridGroupIds.find((gridId) => {
            optionGroup = this.get(event.optionGroups, event.sport.id, event.live, gridId);
            return this.isBettableOptionGroup(optionGroup) && !this.hasNonBettableOdds(optionGroup);
        });

        if (availableOptionGroup) {
            fallbackMarketName = this.fallbackMarketsHeaderInfo[availableOptionGroup];
            return { optionGroup, isFallbackMarket: true, fallbackMarketName, isNextGoalMarket: this.isNextGoalMarket(optionGroup) };
        }

        return undefined;
    }

    private isNextGoalMarket(optionGroup: EventOptionGroup | undefined): boolean {
        if (optionGroup) {
            return (
                optionGroup.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === MarketType.XthHappening &&
                optionGroup.parameters?.find((p) => p.key === Tv2MarketParameters.Happening)?.value === HappeningType.Goal &&
                optionGroup.parameters?.find((p) => p.key === Tv2MarketParameters.Period)?.value === PeriodType.RegularTime
            );
        }
        return false;
    }

    private showFallbackMarkets(fallbackGridIds: string[], optionGroup: EventOptionGroup | undefined, event: GridEvent): boolean {
        return !!fallbackGridIds?.length && (!optionGroup || !this.isBettableOptionGroup(optionGroup) || this.hasNonBettableOdds(optionGroup));
    }
}
